import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Img } from "components";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { setBankDocument } from "redux/features/RefundOrder";
import { convertFileToBase64 } from "pages/CreateRemit/payment";
import { useState } from "react";

interface BankDocument {
  name: string;
  file: FileList | null;
  id: number;
}
interface FormValues {
  cancelled_cheque: BankDocument;
  bank_status_document: BankDocument;
  passbook_front?: BankDocument;
}

export default function BankDocumentsDialog() {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      cancelled_cheque: {
        id: 50,
        name: "Cancelled Cheque",
        file: null,
      },
      bank_status_document: {
        id: 52,
        name: "Bank Status Document",
        file: null,
      },
      passbook_front: {
        id: 51,
        name: "Passbook Front",
        file: null,
      },
    },
  });

  // Use watch to get current file values
  const watch_cancelled_cheque = watch("cancelled_cheque");
  const watch_bankStatusFiles = watch("bank_status_document");
  const watch_passbook_front = watch("passbook_front");
  const [open, setOpen] = useState(false);

  const dispatch = useDispatch();

  // Extract single file objects for display
  const cancelled_cheque = watch_cancelled_cheque?.file?.[0];
  const bankStatusFile = watch_bankStatusFiles?.file?.[0];
  const passbook_front = watch_passbook_front?.file?.[0];

  const onSubmit = async (data: FormValues) => {
    const dataToSet = await Promise.all(
      Object.entries(data).map(async ([, value]) => {
        if (value.file && value.file[0]) {
          // Check if the file exists
          const file = await convertFileToBase64(value.file[0]);
          return {
            name: value.name,
            file: file,
            doc: value.file[0],
            id: value.id,
          };
        }
        return null; // Skip if no file is provided
      }),
    );
    const filteredData = dataToSet.filter(
      (item): item is NonNullable<typeof item> => item !== null,
    );
    dispatch(setBankDocument(filteredData));
    setOpen(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger className="" onClick={() => setOpen(true)}>
        <button className="w-fit text-sm font-semibold rounded-lg text-blue-700">
          + New Bank Account
        </button>
      </AlertDialogTrigger>
      <AlertDialogPortal>
        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
        <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-md animate-contentShow">
          <AlertDialogHeader className="flex text-left gap-4 ">
            <AlertDialogTitle className="font-bold border-b w-full text-xl pt-8 pb-4 px-10 leading-7 tracking-[2%] text-black-label">
              Add Payment Details
            </AlertDialogTitle>
          </AlertDialogHeader>
          <form className="max-w-md" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col text-left gap-4 px-10">
              <div className="flex gap-2 flex-1 flex-grow flex-col w-full">
                <h3 className="text-sm font-medium leading-4">
                  Cancelled cheque
                </h3>
                <div
                  className={`py-1.5 px-3.5 justify-between rounded-md flex border ${errors.cancelled_cheque ? "border-red-500" : "border-[#DCDEE2]"} ${cancelled_cheque ? "bg-[#F2F7FF]" : "bg-[#fff]"}`}
                >
                  <div className="flex gap-3 items-center overflow-hidden">
                    <Img src="/images/remit/file.svg" alt="" />
                    <p className="text-ellipsis truncate">
                      {cancelled_cheque?.name || "No file selected"}
                    </p>
                  </div>
                  <label
                    htmlFor="cancelled-cheque-document"
                    className="cursor-pointer text-blue-700 border border-blue-700 ml-2 px-4 py-2 rounded-lg whitespace-nowrap"
                  >
                    {cancelled_cheque ? "Re-upload" : "Upload"}
                  </label>
                  <input
                    {...register("cancelled_cheque.file", {
                      required: "Cancelled cheque document is required",
                    })}
                    type="file"
                    id="cancelled-cheque-document"
                    className="hidden"
                    accept="image/*"
                  />
                </div>
                {errors.cancelled_cheque?.file && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.cancelled_cheque.file.message as string}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-1 flex-grow flex-col w-full">
                <h3 className="text-sm font-medium leading-4">
                  Bank Account Status
                </h3>
                <div
                  className={`py-1.5 px-3.5 justify-between rounded-md flex border ${errors.bank_status_document ? "border-red-500" : "border-[#DCDEE2]"} ${bankStatusFile ? "bg-[#F2F7FF]" : "bg-[#fff]"}`}
                >
                  <div className="flex gap-3 items-center overflow-hidden">
                    <Img src="/images/remit/file.svg" alt="" />
                    <p className="text-ellipsis truncate">
                      {bankStatusFile?.name || "No file selected"}
                    </p>
                  </div>
                  <label
                    htmlFor="bank-status-document"
                    className="cursor-pointer text-blue-700 border border-blue-700 ml-2 px-4 py-2 rounded-lg whitespace-nowrap"
                  >
                    {bankStatusFile ? "Re-upload" : "Upload"}
                  </label>
                  <input
                    {...register("bank_status_document.file", {
                      required: "Bank status document is required",
                    })}
                    type="file"
                    id="bank-status-document"
                    className="hidden"
                    accept="image/*"
                  />
                </div>
                {errors.bank_status_document?.file && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.bank_status_document.file.message as string}
                  </p>
                )}
              </div>
              <div className="flex gap-2 flex-1 flex-grow flex-col w-full">
                <h3 className="text-sm font-medium leading-4">
                  Passbook front (optional)
                </h3>
                <div
                  className={`py-1.5 px-3.5 justify-between rounded-md flex border border-[#DCDEE2] ${passbook_front ? "bg-[#F2F7FF]" : "bg-[#fff]"}`}
                >
                  <div className="flex gap-3 items-center overflow-hidden">
                    <Img src="/images/remit/file.svg" alt="" />
                    <p className="text-ellipsis truncate">
                      {passbook_front?.name || "No file selected"}
                    </p>
                  </div>
                  <label
                    htmlFor="passbook-front-document"
                    className="cursor-pointer text-blue-700 border border-blue-700 ml-2 px-4 py-2 rounded-lg whitespace-nowrap"
                  >
                    {passbook_front ? "Re-upload" : "Upload"}
                  </label>
                  <input
                    {...register("passbook_front.file")}
                    type="file"
                    id="passbook-front-document"
                    className="hidden"
                    accept="image/*"
                  />
                </div>
                {errors.passbook_front?.file && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.passbook_front.file.message as string}
                  </p>
                )}
              </div>
            </div>
            <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
              <AlertDialogCancel
                className="m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                asChild
              >
                <button
                  type="button"
                  onClick={() => {
                    reset();
                  }}
                >
                  Cancel
                </button>
              </AlertDialogCancel>
              <button
                type="submit"
                className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
              >
                Submit
              </button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
